/**
 * Çalışan–merkez atama geçmişi.
 *
 * Bir çalışan bir merkezden çıkarıldığında kaydı silmiyoruz: sildiğimizde o
 * merkezde yaptığı işler (yüklenen dosyalar, FTE/tarife geçmişi, atanma tarihi,
 * SMO bilgisi) da yok oluyordu. Bunun yerine kaydı yerinde bırakıp
 * `unassignedAt` damgası vuruyoruz — `Centers` koleksiyonundan bir merkez
 * silindiğinde kullanılan `centerDeletedAt` deseninin aynısı.
 *
 * Bu damgayı taşıyan satırlar "arşivlenmiş" atamalardır: her yerde görünmeye
 * devam ederler, sadece "Ayrıldı" rozetiyle işaretlenirler.
 *
 * Damga hem `Projects/{id}.employees[]` hem de `Employees/{id}.projects[]`
 * satırlarına yazılır; iki dizi birbirinin kopyasıdır.
 */

/** Görsel rozet metni. Tek yerden gelsin ki her sayfada aynı yazsın. */
export const ARCHIVED_ASSIGNMENT_LABEL = 'Ayrıldı';

/** Çalışan bu merkezden ayrılmış mı? */
export const isArchivedAssignment = (entry) => Boolean(entry?.unassignedAt);

/** Hâlâ aktif olarak çalıştığı atamalar. */
export const getActiveAssignments = (entries = []) =>
  entries.filter((entry) => !isArchivedAssignment(entry));

/** Ayrıldığı atamalar. */
export const getArchivedAssignments = (entries = []) =>
  entries.filter(isArchivedAssignment);

/**
 * Ayrılma damgasını vurur. Zaten arşivlenmiş bir kaydın ilk ayrılma tarihi
 * korunur — düzenleme ekranı her kaydedildiğinde tarih ileri kaymasın.
 */
export const archiveAssignment = (entry, when = new Date().toISOString()) => ({
  ...entry,
  unassignedAt: entry?.unassignedAt || when,
});

/**
 * Damgayı kaldırır (geri alma). `unassignedAt` alanı Firestore'a `undefined`
 * yazılamayacağı için nesneden tamamen çıkarılır.
 */
export const restoreAssignment = (entry) => {
  if (!entry) return entry;
  const { unassignedAt, ...rest } = entry;
  return rest;
};

/** Rozetin yanında gösterilecek tarih: "10.08.2026". */
export const formatUnassignedDate = (entry) => {
  const raw = entry?.unassignedAt;
  if (!raw) return '';

  const date = raw?.toDate ? raw.toDate() : new Date(raw);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString('tr-TR');
};

/**
 * Rozet için hazır metin: "Ayrıldı (10.08.2026)".
 * Tarih okunamazsa sadece "Ayrıldı" döner.
 */
export const getArchivedAssignmentLabel = (entry) => {
  const date = formatUnassignedDate(entry);
  return date
    ? `${ARCHIVED_ASSIGNMENT_LABEL} (${date})`
    : ARCHIVED_ASSIGNMENT_LABEL;
};
